// Removed Nord component initialization attempt
// import { defineCustomElements, applyPolyfills } from '@nordhealth/components/loader';

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { toast, ToastContainer } from 'react-toastify'
// Import Nord UI CSS first to ensure proper CSS cascade
import '@nordhealth/css'
// Import application styles
import './index.css'
import './styles/global.css'
import './styles/nord-theme.css'
import './styles/responsive.css'
import './styles/appointments.css'
import './styles/booking.css'
import './styles/alerts.css'
import './styles/focus-visible.css'
import './styles/high-contrast.css'
// Remove JSON Server adapter import
import App from './App'
import { ThemeProvider } from './contexts/ThemeContext'
// import { ColorModeProvider } from './contexts/ColorModeContext' // Removed unused import
import { AuthProvider } from './contexts/auth-context'
import AlertSystem from './components/AlertSystem'
import NordProvider from './components/ui/NordProvider'
import { ViewportProvider } from './components/layout/ViewportHandler'
// Use default import for ErrorBoundary based on linter feedback
import ErrorBoundary from './components/ui/ErrorBoundary'
import { NavigationProvider } from './contexts/navigation-context'
import { allNavigationItems } from './constants/navigation-groups'
// Import i18n configuration
import './i18n';
// Import TooltipProvider directly from Radix UI
import { TooltipProvider } from '@radix-ui/react-tooltip';
// Import accessibility initialization
import initAccessibilitySettings from './utils/accessibility-init';

// Navigation items derived from grouped navigation config
const navigationItems = allNavigationItems;

// Removed Nord initialization call
// applyPolyfills().then(() => {
//   defineCustomElements(window);
// });

// Initialize accessibility settings
initAccessibilitySettings();

// React Query Client Setup
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: Error) => {
      console.error('React Query Cache Error:', error);
      toast.error(`Data cache error: ${error.message}`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: Error) => {
      console.error('React Query Mutation Error:', error);
      toast.error(`Data mutation error: ${error.message}`);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
    },
  },
})

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Define a simple loading fallback component for Suspense
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    {/* TODO (i18n): This fallback appears before i18n is loaded, so it can't be translated easily */}
    <div>Loading...</div> 
  </div>
);

// The App component itself already includes ColorModeProvider, so we should remove it here
// to avoid duplicate context providers
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    {/* Wrap the app in Suspense for i18n loading */}
    <React.Suspense fallback={<LoadingFallback />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <NavigationProvider items={navigationItems}>
                <NordProvider>
                  <ViewportProvider>
                    <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
                    <TooltipProvider> 
                      <ErrorBoundary>
                        <App />
                      </ErrorBoundary>
                    </TooltipProvider>
                    <AlertSystem />
                  </ViewportProvider>
                </NordProvider>
              </NavigationProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.Suspense>
  </React.StrictMode>,
) 